import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import ProductSelector from '@components/ProductSelector'
import useProductSelector from '@components/ProductSelector/useProductSelector'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useCreateOrEditAgencyProviderPriceOverrideModal from './hooks/useCreateOrEditAgencyProviderPriceOverrideModal'

const MODAL_KEY = 'CreateOrEditAgencyProviderPriceOverrideModal'

type ModalCallbacks = {
  closeModal: () => void,
  createAgencyProviderPriceOverride: (payload: HandleCallbackActionParams) => void,
  updateAgencyProviderPriceOverride: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditAgencyProviderPriceOverrideModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createAgencyProviderPriceOverride, updateAgencyProviderPriceOverride } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const agencyProviderPriceOverride = digObject(modalPayload, 'agencyProviderPriceOverride', {})

  const newRecord = !agencyProviderPriceOverride.id

  const productSelectorPayload = useProductSelector()
  const { state } = productSelectorPayload

  const useAgencyProviderPriceOverridePayload = useCreateOrEditAgencyProviderPriceOverrideModal({
    agencyProviderPriceOverride,
    state,
  })
  const {
    callbacks: {
      createAgencyProviderPriceOverride: createFn,
      deleteAgencyProviderPriceOverride: deleteFn,
      mapStateToPriceOverride,
      updateAgencyProviderPriceOverride: updateFn,
    },
    creating,
    deleting,
    entityState: {
      price_override,
    },
    errors,
    handlers,
    providerId,
    saveEnabled,
    updating,
  } = useAgencyProviderPriceOverridePayload

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      action: deleteFn,
    },
    strings: { entityName: 'Price Override' },
  }

  const createOrUpdatePriceOverridePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: mapStateToPriceOverride(),
    requestOptions: defaultRequestOptions.agencyProviderPriceOverride,
    toastText: `Price Override ${newRecord ? 'Created' : 'Updated'}`,
  }

  const saveButtonEnabled = newRecord ? saveEnabled && price_override : true

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Price Override"
      />

      <SidebarModal.Content>
        <Form>
          {newRecord && (
            <ProductSelector
              productSelectorPayload={productSelectorPayload}
              providerId={providerId}
            />
          )}

          {saveEnabled && (
            <Form.Field
              label="Price Override"
              errorMessage={errors.price_override}
              marginTop={newRecord ? 'large' : 0}
            >
              <input name="price_override" type="text" value={price_override} {...handlers} />
            </Form.Field>
          )}

          {!newRecord && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                // @ts-expect-error TS2345
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="medium"
              >
                Delete Price Override
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveButtonEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createAgencyProviderPriceOverride(createOrUpdatePriceOverridePayload)
              : () => updateAgencyProviderPriceOverride(createOrUpdatePriceOverridePayload)
          }
          size="large"
        >
          {saveButtonEnabled
            ? `${newRecord ? 'Create' : 'Update'} Price Override`
            : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyProviderPriceOverrideModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
